import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { adminService } from '../../services/adminService';

const WebhooksManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [form, setForm] = useState<{ name: string; url: string; secret?: string; active?: boolean }>({ name: '', url: '', secret: '', active: true });

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminService.listWebhooks();
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    if (!form.name || !form.url) {
      toast.error('Name and URL are required');
      return;
    }
    setLoading(true);
    try {
      await adminService.createWebhook({
        name: form.name.trim(),
        url: form.url.trim(),
        secret: form.secret?.trim() || undefined,
        active: !!form.active,
      });
      toast.success('Webhook created');
      setForm({ name: '', url: '', secret: '', active: true });
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create webhook');
    } finally {
      setLoading(false);
    }
  };

  const onToggleActive = async (id: string, active: boolean) => {
    setLoading(true);
    try {
      await adminService.updateWebhook(id, { active });
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update webhook');
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!window.confirm('Delete this webhook?')) return;
    setLoading(true);
    try {
      await adminService.deleteWebhook(id);
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete webhook');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Webhook Endpoints</h2>
      <div className="grid gap-3 max-w-2xl mb-6">
        <input
          className="border rounded px-3 py-2"
          placeholder="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          disabled={loading}
        />
        <input
          className="border rounded px-3 py-2"
          placeholder="https://..."
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          disabled={loading}
        />
        <input
          className="border rounded px-3 py-2"
          placeholder="Secret (optional)"
          value={form.secret}
          onChange={(e) => setForm({ ...form, secret: e.target.value })}
          disabled={loading}
        />
        <label className="inline-flex items-center space-x-2">
          <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} disabled={loading} />
          <span>Active</span>
        </label>
        <button onClick={onCreate} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 w-max">{loading ? 'Saving...' : 'Create Webhook'}</button>
      </div>

      <div className="overflow-x-auto bg-white shadow rounded">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((w) => (
              <tr key={w.id}>
                <td className="px-4 py-2">{w.name}</td>
                <td className="px-4 py-2 text-blue-700 break-all">{w.url}</td>
                <td className="px-4 py-2">
                  <button onClick={() => onToggleActive(w.id, !w.active)} className={`px-2 py-1 rounded text-white ${w.active ? 'bg-green-600' : 'bg-gray-400'}`}>{w.active ? 'Active' : 'Inactive'}</button>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => onDelete(w.id)} className="px-3 py-1 bg-red-600 text-white rounded">Delete</button>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan={4} className="px-4 py-4 text-center text-gray-500">No webhooks configured</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WebhooksManagement;
